/**
 * HTTP transport — batched POST to the backend ingest endpoint.
 *
 * The events end up in Cloud Logging and from there in the
 * `the-home-ceo.server_logs` BigQuery sink, alongside the iOS and Android
 * backend traffic. The browser never writes to that sink directly; it posts
 * to a Cloud Function which logs each event with the backend's structured
 * logger. See src/logging/README.md for the endpoint contract.
 *
 * ## Why two delivery mechanisms
 *
 * The session-end event fires at `visibilitychange -> hidden`, which is the
 * last moment the page is guaranteed to exist. That single requirement
 * drives most of what looks fussy here:
 *
 * - **While the page is alive**, a normal `fetch` carries the App Check
 *   token in the `X-Firebase-AppCheck` header. A custom header forces a
 *   CORS preflight, which is fine — the browser caches it and there is time
 *   for two round trips.
 * - **At `hidden`**, `navigator.sendBeacon` with a `text/plain` body. That
 *   combination is a CORS *simple* request, so it needs no preflight, and
 *   the browser owns the request from that point on — it completes even if
 *   the tab is gone a millisecond later. The cost is that beacons cannot
 *   set headers, which is why the endpoint also accepts the token as an
 *   `app_check_token` body field, and why the token is cached ahead of
 *   time: there is no opportunity to await one on the way out.
 *
 * Retries only ever happen on the live-page path. Retrying at unload would
 * be scheduling work for a page that will not be there to run it.
 *
 * @module logging/transports/http-transport
 */

import { getToken } from 'firebase/app-check';
import { appCheck } from '@config/firebase-config';
import type { BatchSink, FlushReason, WireEvent } from '../core/types';
import { isTerminalFlush } from '../core/types';
import { diagnostic } from '../core/diagnostic';

/** Total attempts on the live-page path, including the first. */
const MAX_ATTEMPTS = 3;

/** First retry delay; doubles each attempt. */
const BASE_BACKOFF_MS = 1_000;

/** Ceiling on how long a cached App Check token is reused, whatever its own
 *  expiry says. Keeps a comfortable margin without refreshing on every flush. */
const MAX_TOKEN_CACHE_MS = 20 * 60_000;

/** Stop reusing a token this long before it actually expires, so one that is
 *  valid when a beacon is queued is still valid when it is delivered. */
const TOKEN_EXPIRY_MARGIN_MS = 60_000;

/**
 * Milliseconds-since-epoch at which an App Check token stops being valid,
 * read from its own `exp` claim, or null if it cannot be read.
 *
 * The cache used to be a flat 20 minutes with no reference to the token it
 * was holding. Tokens are minted with an hour of life, so the common case
 * had 40 minutes of headroom -- but `getToken(appCheck, false)` returns
 * whatever the SDK already has, and nothing in its contract promises that is
 * fresh. A token handed over with ten minutes left would have been reused
 * for twenty, sending an expired credential for the last ten.
 *
 * That mattered more than an ordinary staleness bug because of the
 * asymmetry at the endpoint: a batch with **no** token is accepted and
 * tagged `app_check: "absent"`, while a batch with an **invalid** token is
 * rejected outright. So an expired token does not degrade the record, it
 * destroys the batch -- strictly worse than sending none.
 */
function tokenExpiryMs(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const claims: unknown = JSON.parse(atob(padded));
    const exp = (claims as { exp?: unknown }).exp;
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1_000 : null;
  } catch {
    // A token this code cannot parse is still a token the endpoint may
    // accept; fall back to the ceiling rather than refusing to send one.
    return null;
  }
}

export interface HttpTransportOptions {
  readonly endpoint: string;
}

export function createHttpTransport(options: HttpTransportOptions): BatchSink {
  let cachedToken: string | null = null;
  /** When the cached token stops being reusable — the earlier of its own
   *  expiry (less a margin) and the flat ceiling. */
  let reusableUntil = 0;
  let refreshing: Promise<void> | null = null;

  // ---------------------------------------------------------------------
  // App Check token
  // ---------------------------------------------------------------------

  /**
   * Refresh the cached token, at most one refresh in flight at a time.
   *
   * A failure here is not worth surfacing: App Check may be uninitialized
   * (no site key configured locally), and the endpoint decides what an
   * unattested request is worth. Sending without a token and letting the
   * server reject is more useful than dropping the event on the client.
   */
  function refreshToken(): Promise<void> {
    if (refreshing) return refreshing;
    if (!appCheck) return Promise.resolve();

    refreshing = getToken(appCheck, false)
      .then((result) => {
        cachedToken = result.token;
        const ceiling = Date.now() + MAX_TOKEN_CACHE_MS;
        const expiry = tokenExpiryMs(result.token);
        reusableUntil =
          expiry === null
            ? ceiling
            : Math.min(ceiling, expiry - TOKEN_EXPIRY_MARGIN_MS);
      })
      .catch((error: unknown) => {
        diagnostic('app check token unavailable', error);
      })
      .finally(() => {
        refreshing = null;
      });

    return refreshing;
  }

  /**
   * Token if one is cached and still comfortably valid, else null — never
   * blocks.
   *
   * Returns null rather than the stale token when the cache has aged out.
   * Sending nothing is the better failure: the endpoint accepts a tokenless
   * batch and tags it, and rejects an invalid one.
   */
  function tokenNow(): string | null {
    if (cachedToken && Date.now() < reusableUntil) return cachedToken;
    // Warm the cache for the next call, including the unload path that
    // cannot await anything.
    void refreshToken();
    return null;
  }

  // ---------------------------------------------------------------------
  // Serialization
  // ---------------------------------------------------------------------

  function serialize(batch: readonly WireEvent[], token: string | null): string {
    return JSON.stringify(
      token ? { events: batch, app_check_token: token } : { events: batch },
    );
  }

  // ---------------------------------------------------------------------
  // Delivery
  // ---------------------------------------------------------------------

  /**
   * Unload path. Synchronous, unretried, no headers.
   *
   * `sendBeacon` returns false when the browser's beacon queue is full,
   * which is the one case worth falling back on — `keepalive` fetch has a
   * separate budget and may still get through.
   */
  function sendAtUnload(batch: readonly WireEvent[]): void {
    const payload = serialize(batch, tokenNow());

    try {
      if (typeof navigator.sendBeacon === 'function') {
        // text/plain keeps this a CORS simple request: no preflight, which
        // there is no time for here.
        const blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(options.endpoint, blob)) return;
        diagnostic('beacon queue full, falling back to keepalive fetch');
      }

      void fetch(options.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: payload,
        keepalive: true,
        mode: 'cors',
      }).catch(() => {
        // The page is going away; there is nobody left to tell.
      });
    } catch (error) {
      diagnostic('unload delivery failed', error);
    }
  }

  /**
   * Live-page path. Retries transient failures with exponential backoff.
   *
   * A 4xx other than 429 is a contract violation — a malformed batch, or an
   * event name the endpoint does not allow — and retrying it would repeat
   * the same rejection forever. Those are dropped after one attempt.
   */
  async function sendLive(batch: readonly WireEvent[], attempt = 1): Promise<void> {
    const token = tokenNow() ?? (await refreshToken().then(() => cachedToken));

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['X-Firebase-AppCheck'] = token;

    try {
      const response = await fetch(options.endpoint, {
        method: 'POST',
        headers,
        body: serialize(batch, token),
        keepalive: true,
        mode: 'cors',
      });

      if (response.ok) return;

      const retryable = response.status === 429 || response.status >= 500;
      if (retryable && attempt < MAX_ATTEMPTS) {
        await backoff(attempt);
        return sendLive(batch, attempt + 1);
      }

      if (response.status === 401 || response.status === 403) {
        // Most likely a stale App Check token. Drop the cache so the next
        // batch mints a fresh one rather than repeating the rejection.
        cachedToken = null;
        reusableUntil = 0;
      }

      diagnostic(`ingest rejected ${String(response.status)}, dropped ${String(batch.length)}`);
    } catch (error) {
      // Network-level failure: offline, DNS, blocked by an extension.
      if (attempt < MAX_ATTEMPTS) {
        await backoff(attempt);
        return sendLive(batch, attempt + 1);
      }
      diagnostic(`ingest unreachable, dropped ${String(batch.length)}`, error);
    }
  }

  function backoff(attempt: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, BASE_BACKOFF_MS * 2 ** (attempt - 1));
    });
  }

  // ---------------------------------------------------------------------

  // Warm the token cache at construction so the first unload flush — which
  // may be the only one a bouncing visitor produces — has one to hand.
  void refreshToken();

  return {
    name: 'http',

    send(batch: readonly WireEvent[], reason: FlushReason): void {
      if (batch.length === 0) return;

      if (isTerminalFlush(reason)) {
        sendAtUnload(batch);
        return;
      }

      // The Transport contract forbids rejecting; sendLive already handles
      // its own failures, and this guards against an unforeseen throw.
      void sendLive(batch).catch((error: unknown) => {
        diagnostic('unexpected transport failure', error);
      });
    },
  };
}
